import { useState, useMemo } from 'react'

/**
 * Custom hook for handling pagination logic
 * Eliminates duplication of pagination state and calculations across components
 * 
 * @param {Array} data - Array of items to paginate
 * @param {number} pageSize - Number of items per page
 * @returns {Object} Pagination state and utilities
 */
export const usePagination = (data = [], pageSize = 3) => {
  const [currentPage, setCurrentPage] = useState(1)
  
  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(data.length / pageSize))
  
  // Get current page slice
  const currentPageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    const end = start + pageSize
    return data.slice(start, end)
  }, [data, currentPage, pageSize])

  // Reset to page 1 when data changes
  useMemo(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [data.length, currentPage, totalPages])

  return {
    currentPage,
    totalPages,
    currentPageData,
    setCurrentPage,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
    totalItems: data.length
  }
}

export default usePagination
